import React from "react";
//#region types
import { DialogEnums } from "models/enums";
import { DialogProps } from "models/propTypes/dialogPropTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import {
  CommissionDeviationTypeId,
  SavedSalaryDepositCorrectionDeviationTypeId,
} from "utils/constants/salaryConstants";
import { DeviationEmployee } from "models/propTypes/wintSalaryPropTypes";
import { DateRange } from "models/formTypes/util";
import { SalaryDeviationFormTypes } from "models/formTypes";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { Icon, RoundButton } from "components/common";
import { useWintSalaryDeviationService } from "serviceHooks";
import useDeviationPermissions from "components/salaryDeviation/usePermissions";
import WintDeviationCreateFormContainer from "./forms/wintDeviationCreateFormContainer";
import useDeleteConfirmation from "./useDeleteConfirmation";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import { WintSuperkollAPIModelWintSalary } from "wint-api-client/dist/types";
import useSalaryReApproveDialog from "./useSalaryReApproveDialog";
import { displayDialog } from "store/actions/utilActions";
import { useDispatch } from "react-redux";

function useWintDeviationCreateDialog() {
  const dispatch = useDispatch();
  const wintSalaryDeviationService = useWintSalaryDeviationService();
  const { showDeleteConfirmation } = useDeleteConfirmation();
  const getReApproveDialog = useSalaryReApproveDialog();
  const { canManageSalary } = useDeviationPermissions();

  const currentFormValuesRef = React.useRef<SalaryDeviationFormTypes.WintDeviationCreate | undefined>(undefined);
  const initialFormValuesRef = React.useRef<SalaryDeviationFormTypes.WintDeviationCreate | undefined>(undefined);

  const handleFormUpdate = React.useCallback(
    (values: SalaryDeviationFormTypes.WintDeviationCreate, isValid: boolean) => {
      currentFormValuesRef.current = values;
    },
    []
  );

  const handleInitialValuesLoaded = React.useCallback((values: SalaryDeviationFormTypes.WintDeviationCreate) => {
    initialFormValuesRef.current = values;
  }, []);

  const showReApproveDialogIfNeeded = (
    result: WintSuperkollAPIModelWintSalary.SalaryMonthMutationResultBase | undefined,
    onSuccess?: () => void
  ) => {
    const reApproveData = result?.reApprovalInfo;
    if (reApproveData) {
      const { personId, yearMonth } = reApproveData;

      const formValues = currentFormValuesRef.current;
      const personName = Number(formValues?.employee?.id) === personId ? (formValues?.employee?.value ?? "") : "";

      const currentDateRange = currentFormValuesRef.current?.timeDeviation?.dateRange;
      const previousDateRange = initialFormValuesRef.current?.timeDeviation?.dateRange;
      const shownChanges = !!currentDateRange && !!previousDateRange;

      const reApproveDialog = getReApproveDialog(
        personId,
        personName,
        yearMonth,
        shownChanges,
        currentDateRange,
        previousDateRange,
        onSuccess
      );

      setTimeout(() => {
        dispatch(displayDialog({ ...reApproveDialog, onClose: onSuccess }));
      }, 100);
    } else {
      onSuccess?.();
    }
  };

  const onDeleteClick = async (deviationId?: string, onDeleteSuccess?: () => void, closeDialog?: () => void) => {
    const onDelete = async () => {
      let mutationResult: WintSuperkollAPIModelWintSalary.SalaryMonthMutationResultBase | undefined;

      if (deviationId) {
        const formValues = currentFormValuesRef.current;
        const dateRange = formValues?.timeDeviation?.dateRange;
        const isPartialView = formValues && !formValues.isFullDeviationLoaded;

        if (isPartialView && dateRange) {
          const result = await wintSalaryDeviationService.deletePartialDeviationReapprove(
            deviationId,
            dateRange.start,
            dateRange.end
          );
          mutationResult = result.data;
        } else {
          const result = await wintSalaryDeviationService.deleteDeviationReapprove(deviationId);
          mutationResult = result.data;
        }
      }

      closeDialog?.();
      setTimeout(() => {
        showReApproveDialogIfNeeded(mutationResult, onDeleteSuccess);
      });
    };

    const handleDeleteFormSuccess = (result?: WintSuperkollAPIModelWintSalary.SalaryMonthMutationResultBase) => {
      showReApproveDialogIfNeeded(result, onDeleteSuccess);
    };

    const formValues = currentFormValuesRef.current;
    const deleteOptions =
      formValues && deviationId
        ? ({
            deviationId: deviationId,
            deviationType: formValues.deviationType,
            deviation: formValues.deviation,
            dateRange: formValues.timeDeviation?.dateRange,
            selectedPeriod: formValues.timeDeviation?.selectedPeriod,
            isFullDeviationLoaded: formValues.isFullDeviationLoaded,
          } as const)
        : undefined;

    showDeleteConfirmation(onDelete, undefined, deleteOptions, closeDialog, handleDeleteFormSuccess);
  };

  const getDeviationCreateDialog = (
    deviationId?: string,
    employee?: DeviationEmployee,
    deviationTypesId?: string,
    onDeviationSubmitSuccess?: () => void,
    filterPeriod?: DateRange,
    lockedPeriod?: boolean
  ): DialogProps => {
    const isEditing = !!deviationId;

    // Two independent read-only causes: a system-managed deviation type, or an
    // approved/finalized salary period. Only the latter gets the explanatory message —
    // system-managed types are read-only by design and already understood.
    const isSystemManagedType = wintSalaryDeviationService.excludedDeviationTypeIds.includes(deviationTypesId);
    const isReadOnly = isSystemManagedType || !!lockedPeriod;
    // Wint.Salary refuses deleting the saved-salary correction outright, so that one stays hidden.
    // Commission is deletable again (DEV-30494) — deleting it now records the deal as withdrawn, so the
    // engine no longer writes the row back — but only for a user who may manage salary, matching Hjärtat
    // where the commission types were admin-only. Display only: Wint.Salary enforces the same rule.
    // Keyed on the type id: the footer is built before the deviation loads.
    const canDelete =
      isEditing &&
      !lockedPeriod &&
      (deviationTypesId !== CommissionDeviationTypeId || canManageSalary) &&
      deviationTypesId !== SavedSalaryDepositCorrectionDeviationTypeId;

    const handleDeviationSubmit = (result?: WintSuperkollAPIModelWintSalary.SalaryMonthMutationResultBase) => {
      showReApproveDialogIfNeeded(result, onDeviationSubmitSuccess);
    };

    const getTitleKey = () => {
      if (!isEditing) return TranslationKeys.SalaryDeviation.CreateDeviation;
      return lockedPeriod
        ? TranslationKeys.SalaryDeviation.ViewDeviation
        : TranslationKeys.SalaryDeviation.EditDeviation;
    };

    return {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(getTitleKey()),
      titleIcon: IconDefinitionConstants.WCalendar,
      // A locked period offers nothing to submit — leave only the close button.
      cancelButtonText: lockedPeriod
        ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Close)
        : undefined,
      actions: lockedPeriod
        ? []
        : [
            {
              text: LocalizationHelperInstance.getFormattedMessage(
                isEditing ? TranslationKeys.Common.Save : TranslationKeys.Common.Send
              ),
              successMessage: isEditing
                ? TranslationKeys.SalaryDeviation.DeviationUpdatedSuccess
                : TranslationKeys.SalaryDeviation.DeviationCreatedSuccess,
              disabled: isReadOnly,
            },
          ],
      footer: canDelete
        ? (closeDialog, isValid) => (
            <Grid item xs alignItems="flex-end">
              <Grid item>
                <RoundButton
                  className={cssExports.delete}
                  disabled={!isValid}
                  onClick={() => {
                    onDeleteClick(deviationId, onDeviationSubmitSuccess, closeDialog);
                  }}
                >
                  <Icon icon={IconDefinitionConstants.WTrashAlt} />
                </RoundButton>
              </Grid>
            </Grid>
          )
        : undefined,
      dialogType: DialogEnums.DialogType.Content,
      children: (
        <WintDeviationCreateFormContainer
          deviationId={deviationId}
          employee={employee}
          readOnly={isReadOnly}
          lockedPeriod={lockedPeriod}
          filterPeriod={filterPeriod}
          onFormUpdate={handleFormUpdate}
          onInitialValuesLoaded={handleInitialValuesLoaded}
        />
      ),
      maxWidth: "sm",
      onSubmit: handleDeviationSubmit,
    };
  };

  return getDeviationCreateDialog;
}

export default useWintDeviationCreateDialog;
